import { useCallback, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { isPolicyDetailsV2Enabled } from '@/constants/featureFlags';
import { useAppState } from '@/lib/legacy';
import { PolicyDetailsPage, PolicyPage } from '../pages';

/** Which policy page a user sees while the redesign is behind VITE_POLICY_DETAILS_V2. */
export type PolicyView = 'new' | 'legacy';

const SECTION_PATH = /^\/policies\/([^/]+)\/(endorsements|reporting|documents)\/?$/;

/** The closest URL today's page has: /policies/:policyId for a section path, anything else unchanged. */
export const legacyPathFor = (pathname: string, policyId: string): string => {
  const match = SECTION_PATH.exec(pathname);
  return match && match[1] === policyId ? `/policies/${policyId}` : pathname;
};

/**
 * The floating control that moves between the two policy pages, fixed to the viewport's bottom-right:
 * a "New policy page" switch, on in the new view and off in the old one.
 */
export function ViewSwitch({ view, onSwitch }: { view: PolicyView; onSwitch: (to: PolicyView) => void }) {
  const id = useId();
  /* Portalled to <body>: the dashboard's detail region is a ScrollView with transform: translateZ(0),
     which would make position: fixed relative to the region rather than the viewport. */
  return createPortal(
    <div className="fixed right-4 bottom-4 z-[1200] flex items-center gap-2 rounded-full border bg-background px-4 py-2 text-[13px] shadow-md">
      <Switch id={id} size="sm" checked={view === 'new'} onCheckedChange={(checked) => onSwitch(checked ? 'new' : 'legacy')} />
      <Label htmlFor={id} className="text-[13px] font-normal">
        New policy page
      </Label>
    </div>,
    document.body
  );
}

/**
 * The one place that knows two policy pages exist. With the flag off, today's page, as it is now.
 * With it on, the page the user chose (the new one unless they chose 'legacy'), under the view switch.
 * Today's page has no section routes, so on it a section URL becomes the policy's own URL.
 */
export function PolicyRoute() {
  const { policyId } = useParams<{ policyId: string }>();
  const { pathname } = useLocation();
  const [appState] = useAppState();
  const flagOn = isPolicyDetailsV2Enabled();
  // settings is a ref, so writing it does not re-render: the view lives in state, seeded from it.
  const [chosen, setChosen] = useState<PolicyView>(() => (appState.settings?.current.policyView === 'legacy' ? 'legacy' : 'new'));
  const view: PolicyView = flagOn ? chosen : 'legacy';

  const onSwitch = useCallback(
    (to: PolicyView) => {
      appState.saveSettings?.({ policyView: to });
      setChosen(to);
    },
    [appState]
  );

  if (view === 'legacy') {
    const legacyPath = legacyPathFor(pathname, policyId);
    if (legacyPath !== pathname) {
      return <Redirect to={legacyPath} />;
    }
  }

  return (
    <>
      {view === 'new' ? <PolicyDetailsPage /> : <PolicyPage />}
      {flagOn ? <ViewSwitch view={view} onSwitch={onSwitch} /> : null}
    </>
  );
}
